import React, { useState, useEffect } from "react"
import { ExtractId } from "../../../util"
import Paginator from "../../Paginator"
import { getManageCatvList } from "../../../api/manage_catv_list"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import ManageCatvCreate from "./create"
import ManageCatvUpdate from "./update"
import ManageCatvDelete from "./delete"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

export interface ManageCatvData {
  id: string
  type: string
  name: string
  address: string
  phone: string
  detection_enabled: number
  detection_period: string[]
  sensitivity_enabled: number
  sensitivity: number
  option_enabled: number
  option_period: string[]
  critical_count_enabled: number
  critical_count: number
  create_date: number
  update_date: number
  is_delete: number
}

const ManageCatvTileList: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const [errorMessage, setErrorMessage] = useState("")
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [catvData, setCatvData] = useState<ManageCatvData[]>([])
  const [filteredData, setFilteredData] = useState<ManageCatvData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const [catvCreate, setCatvCreate] = useState<boolean>(false)
  const [catvEdit, setCatvEdit] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [catvDelete, setCatvDelete] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [selectedCatv, setSelectedCatv] = useState<ManageCatvData | null>(null) // 編集対象のサービス提供元データ

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const fetchManageCatvList = async () => {
    try {
      startLoading()
      const manageCatvData = await getManageCatvList()
      setCatvData(manageCatvData.catv_list)

      return manageCatvData.catv_list
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchManageCatvList()
  }, [])

  // 検索処理
  const handleSearch = () => {
    const filtered = catvData.filter((catv) => {
      const matchesText =
        searchText === "" ||
        catv.name?.includes(searchText) ||
        catv.address?.includes(searchText) ||
        catv.phone?.includes(searchText)

      return matchesText
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    setFilteredData(catvData)
  }, [catvData])

  const handleCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setCatvCreate(true)
  }

  const handleEdit = (id: string) => {
    //console.log("編集ボタンがクリックされた " + id);
    const catvToEdit = catvData.find((catv) => catv.id === id)
    if (catvToEdit) {
      setSelectedCatv(catvToEdit) // 編集対象をセット
      setCatvEdit(true) // 編集モーダルを表示
    }
  }

  const handleDelete = (id: string) => {
    //console.log("削除ボタンがクリックされた " + id);
    const catvToEdit = catvData.find((catv) => catv.id === id)
    if (catvToEdit) {
      setSelectedCatv(catvToEdit) // 編集対象をセット
      setCatvDelete(true) // 編集モーダルを表示
    }
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await fetchManageCatvList()

      // データ更新後に検索を行う
      setCatvCreate(false)
      setCatvEdit(false)
      setCatvDelete(false)

      // フィルタリングを再実行して、画面を更新
      handleSearch()
    } else {
      setCatvCreate(false)
      setCatvEdit(false)
      setCatvDelete(false)
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  const validate = (message: string) => {
    setSearchText(message)

    if (message.length > 10) {
      setErrorMessage("入力文字数は10文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  return (
    <DashboardLayout>
      {catvCreate && <ManageCatvCreate onClose={handleClose} />}
      {catvEdit && selectedCatv && (
        <ManageCatvUpdate
          onClose={handleClose}
          catvData={selectedCatv} // 編集するサービス提供元データを渡す
        />
      )}
      {catvDelete && selectedCatv && (
        <ManageCatvDelete
          onClose={handleClose}
          catvData={selectedCatv} // 削除するサービス提供元データを渡す
        />
      )}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            サービス提供元一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォーム */}
      <MDBox
        mb={2}
        display="flex"
        gap={3}
        flexWrap="wrap"
        alignItems="center"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <MDInput
              label="サービス提供元名や住所で検索"
              value={searchText}
              onChange={(e) => validate(e.target.value)}
              sx={{ width: "240px", backgroundColor: "#FFFFFF", borderRadius: "10px" }}
              error={Boolean(errorMessage)} // エラー時のスタイル変更
            />
            {errorMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "100%", // 入力欄の下に配置
                  left: "0",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            )}
          </div>
        </MDBox>
        <MDButton variant="gradient" color="info" size="medium" onClick={handleSearch} disabled={errorMessage !== ""}>
          <Icon>search</Icon>&nbsp; 検索
        </MDButton>
        <MDBox sx={{ width: "calc(100% - 482px)" }} />
        <MDBox>
          <MDButton variant="gradient" color="info" size="medium" onClick={handleCreate}>
            追加
          </MDButton>
        </MDBox>
      </MDBox>
      <MDBox mt={4}>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">サービス提供元ID</TableCell>
                <TableCell align="left">サービス提供元名</TableCell>
                <TableCell align="left">住所</TableCell>
                <TableCell align="left">電話番号</TableCell>
                <TableCell align="center" style={{ minWidth: "75px" }}>
                  編集
                </TableCell>
                <TableCell align="center" style={{ minWidth: "75px" }}>
                  削除
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={ExtractId(row.id) || undefined}
                    >
                      {ExtractId(row.id)}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.name}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.address}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.phone}</div>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      sx={{ color: "#FFF" }}
                      onClick={() => {
                        handleEdit(`${row.id}`)
                      }}
                    >
                      編集
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      sx={{ color: "#FFF" }}
                      onClick={() => {
                        handleDelete(`${row.id}`)
                      }}
                    >
                      削除
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"ManageCatvTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageCatvTileList
